// Turning stored schedule ROWS into the week they actually COVER.
//
// This exists because the two are not the same thing and the difference is
// invisible. An overnight shift is ONE row stored under the day it STARTS
// (migration 013, read by ON_SHIFT_SQL's third disjunct in
// src/routing/eligibility.js). So "Nights, Mon-Fri" written as weekdays 1-5 covers
// SATURDAY 00:00-06:00 and does NOT cover MONDAY 00:00-06:00 — while the same
// intent written as weekdays 0-4 covers Monday and not Saturday. Both produce five
// identical-looking bars labelled "22:00-06:00" in any UI that draws rows.
//
// No React in here, so it can be tested under node --test: components have no
// coverage in this repository and this task does not add a DOM harness. The same
// reason every sentence the grid renders is decided HERE rather than by a ternary
// inline — see the header of OnShiftPreview.jsx, which learned it the hard way: a
// branch nobody can hold to being total renders a blank cell, and a blank cell
// beside an agent's name reads as "fine".

/** 0 = Sunday .. 6 = Saturday (013:49), matching Postgres EXTRACT(DOW). */
export const WEEKDAY_LABELS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/** A full week of being on shift, which is what NO schedule rows means. */
const HOURS_IN_A_WEEK = 168;

const MINUTES_IN_A_DAY = 24 * 60;

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return (h * 60) + m;
};

const toHhMm = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * The intervals a set of rows actually covers, split at midnight.
 *
 * An overnight row becomes TWO intervals: the rest of its own day, and the
 * beginning of the next — and the next day wraps 6 -> 0, because Saturday night
 * spills into Sunday, not into a weekday 7 that does not exist.
 *
 * `'24:00'` rather than `'00:00'` for a day-ending boundary: an interval from
 * 22:00 to 00:00 reads as zero length or as backwards, and this value is only ever
 * rendered, never bound.
 *
 * An empty input returns an empty array. It deliberately does NOT return "the
 * whole week": the emptiness is the signal every confirmation in this subsystem is
 * built on, and flattening it here would hide it. `weekCoverage` is the caller that
 * says what the emptiness means, in one tested place.
 *
 * Overlapping rows come back overlapping. Merging is `weekCoverage`'s job, because
 * this function's contract is one-interval-per-row-per-day and two of its tests
 * pin exactly that.
 *
 * @param {Array<{weekday: number, start_time: string, end_time: string}>} rows
 * @returns {Array<{weekday: number, from: string, to: string}>}
 */
export function coverageIntervals(rows) {
  const out = [];
  for (const row of rows ?? []) {
    const start = toMinutes(row.start_time);
    const end = toMinutes(row.end_time);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    // A ZERO-LENGTH ROW COVERS NOTHING, and it must not fall into the overnight
    // branch below. `start >= end` reads 09:00-09:00 as a shift that runs to
    // midnight and out the other side: 24 hours of coverage, drawn beside a row
    // this same module labels "covers nothing" — caught by rendering the component
    // and reading the HTML. `agent_schedules_nonempty_check` means this can never
    // be a stored row, so the only place it exists is the editor, mid-edit, which
    // is the one place a lead can still be told.
    if (start === end) continue;
    if (start < end) {
      out.push({ weekday: row.weekday, from: toHhMm(start), to: toHhMm(end) });
    } else {
      // The overnight case. Migration 013 allows start > end precisely for this,
      // and its `agent_schedules_nonempty_check` rules out start === end.
      out.push({ weekday: row.weekday, from: toHhMm(start), to: '24:00' });
      out.push({ weekday: (row.weekday + 1) % 7, from: '00:00', to: toHhMm(end) });
    }
  }
  return out.sort((a, b) => a.weekday - b.weekday || toMinutes(a.from) - toMinutes(b.from));
}

/**
 * Every day's intervals, overlaps merged, as `[[startMinute, endMinute], ...]`.
 *
 * MERGING IS NOT COSMETIC. Nothing refuses overlapping rows: `validateWeek`'s
 * `seen` set and migration 020's unique index catch only an EXACT duplicate, so
 * "09:00-17:00 and 12:00-20:00 on Monday" is a schedule a lead can really save by
 * adding a shift twice and adjusting one of them. Summed as rows that is 16 hours;
 * covered it is 11. The larger number is the one that would go into the
 * confirmation dialog, about an agent who is on shift for the smaller.
 *
 * Touching intervals merge too (09:00-12:00 and 12:00-17:00 is one stretch), which
 * is what makes the panel read as coverage rather than as a list of rows wearing a
 * different label.
 */
function mergedByDay(rows) {
  const byDay = new Map();
  for (const interval of coverageIntervals(rows)) {
    if (!byDay.has(interval.weekday)) byDay.set(interval.weekday, []);
    byDay.get(interval.weekday).push([toMinutes(interval.from), toMinutes(interval.to)]);
  }

  for (const [weekday, spans] of byDay) {
    spans.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const span of spans) {
      const last = merged[merged.length - 1];
      // `>=`, not `>`: two shifts that meet at 12:00 are one stretch of coverage.
      if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1]);
      else merged.push([...span]);
    }
    byDay.set(weekday, merged);
  }
  return byDay;
}

/**
 * The zone a brand-new week is drawn in, until a lead picks another.
 *
 * Pacific, because that is where the desk is. It was Eastern, which was wrong in the
 * quiet way that matters here: a lead who never opens the zone picker gets shifts three
 * hours off, so an agent scheduled 08:00-17:00 is on shift from 05:00 local and
 * routing hands them tickets before they are awake.
 *
 * A WINDOWS id, not IANA. T-SQL's AT TIME ZONE rejects 'America/Los_Angeles' outright
 * and migration 013's CHECK on the column exists to catch that at write time.
 * 'Pacific Standard Time' is the Windows id for US Pacific and observes daylight
 * saving despite the word Standard in its name.
 */
export const DEFAULT_TIMEZONE = 'Pacific Standard Time';

/**
 * How many hours a week these rows cover.
 *
 * NO ROWS IS 0, and it used to be 168.
 *
 * An agent with no schedule is now on shift never, not always (src/routing/eligibility.js
 * — the fail-open default was removed once /admin/schedules existed to fix the gap it
 * was covering for). Every confirmation on this screen is built on this number, so it
 * has to move with the router or the screen starts describing a system that is not
 * running: the old value would have told a lead that clearing a week leaves an agent
 * available 168 hours, when in fact it removes them from rotation entirely.
 *
 * Counted from MERGED COVERAGE rather than by summing row lengths, for the reason
 * `mergedByDay` records: overlapping rows are savable, and summing them reports an
 * agent as available for hours they are not.
 */
export function weeklyHours(rows) {
  if (!rows || rows.length === 0) return 0;
  let minutes = 0;
  for (const spans of mergedByDay(rows).values()) {
    for (const [from, to] of spans) minutes += to - from;
  }
  return minutes / 60;
}

/**
 * Hours as a sentence fragment, never as a raw float.
 *
 * `${weeklyHours(rows)} hours` renders "23.983333333333334 hours" for a single
 * 00:00-23:59 day — in the dialog that decides whether an agent gets tickets. And
 * rounding to a decimal is worse than ugly: seven whole days is 167.88 hours, which
 * `toFixed(1)` turns into "168.0" — the one number in this whole subsystem that
 * means "no schedule at all, on shift always", printed for the schedule that is its
 * opposite. Whole minutes cannot collide.
 */
export function formatHours(hours) {
  const total = Math.round(Number(hours) * 60);
  if (!Number.isFinite(total)) return '0 hours';
  const h = Math.floor(total / 60);
  const m = total % 60;
  const hourPart = `${h} hour${h === 1 ? '' : 's'}`;
  const minutePart = `${m} minute${m === 1 ? '' : 's'}`;
  if (m === 0) return hourPart;
  if (h === 0) return minutePart;
  return `${hourPart} ${minutePart}`;
}

/**
 * The one thing worth saying about a single row, or null.
 *
 * Both cases are about a row whose two times do not read the way they look:
 *
 *   OVERNIGHT — start after end is legal and means the shift crosses midnight into
 *     the NEXT day, which is the whole reason this screen draws coverage. Naming
 *     the day it lands on is the cheapest possible version of that.
 *   ZERO LENGTH — `validateWeek` refuses start === end with "a shift that starts
 *     and ends at the same time is zero length, not all day". True, and the lead
 *     meets it as a 422 about `rows[3].end_time` after pressing Save. Better at the
 *     row, before.
 *
 * A half-typed time (`<input type="time"` hands back `''` while it is cleared) is
 * neither, and must not throw.
 */
export function shiftNote(row) {
  const start = toMinutes(row?.start_time);
  const end = toMinutes(row?.end_time);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (start === end) {
    return 'Zero length: a shift that starts and ends at the same time covers nothing. '
      + 'Use 00:00–23:59 for a whole day.';
  }
  if (start > end) {
    if (!Number.isInteger(row?.weekday)) return 'Overnight, continues into the next day';
    return `Overnight, continues into ${WEEKDAY_LABELS[(row.weekday + 1) % 7]}`;
  }
  return null;
}

/**
 * The whole week, in the exact shape the grid renders — seven days, always, in
 * order, each with a sentence.
 *
 * `neverOnShift` IS THE POINT OF THIS FUNCTION, and it used to be `alwaysOnShift`.
 *
 * `coverageIntervals([])` returns nothing, and what that nothing MEANS is the one
 * thing this screen cannot get wrong. It used to mean on shift 24/7, so drawing seven
 * "Not covered" bars told the lead the precise opposite of the truth. It now means on
 * shift never, so those seven bars are exactly right — but the summary still has to
 * say why the week is empty and what it costs, because seven empty bars read as a
 * drawing problem rather than as an agent routing will never pick.
 *
 * Either way the emptiness is answered here, once, under test, rather than by
 * whichever ternary a component happens to write.
 *
 * @returns {{neverOnShift: boolean, hours: number, summary: string,
 *            days: Array<{weekday: number, label: string,
 *                         spans: Array<{from: string, to: string}>, text: string}>}}
 */
export function weekCoverage(rows) {
  const neverOnShift = !rows || rows.length === 0;
  const hours = weeklyHours(rows);
  const byDay = neverOnShift ? new Map() : mergedByDay(rows);

  const days = WEEKDAY_LABELS.map((label, weekday) => {
    const spans = (byDay.get(weekday) ?? []).map(([from, to]) => ({
      from: toHhMm(from),
      // A day that runs to midnight ends at 24:00, not at a 00:00 that reads as
      // the start of the same day.
      to: to === MINUTES_IN_A_DAY ? '24:00' : toHhMm(to),
    }));
    let text;
    if (spans.length === 0) text = 'Not covered';
    else text = spans.map((span) => `${span.from}–${span.to}`).join(', ');
    return { weekday, label, spans, text };
  });

  const summary = neverOnShift
    ? 'No shifts. An agent with no shifts is never on shift, so routing will skip them '
      + 'and they will not be assigned any ticket. Draw their week below to put them '
      + 'into rotation.'
    : `These shifts cover ${formatHours(hours)} of the week's ${HOURS_IN_A_WEEK}.`;

  return { neverOnShift, hours, summary, days };
}

/**
 * A time zone as the picker lists it, labelled so the near-misses are tellable
 * apart on any day of the year.
 *
 * `Eastern Standard Time (Mexico)` (never daylight saving), `SA Eastern Standard
 * Time` and `AUS Eastern Standard Time` are all real Windows ids, all accepted by
 * `agent_schedules`, and all one scroll from the right answer. Labelling with the
 * LIVE offset is a picker that is safe for eight months a year and useless for the
 * other four: in January `Eastern Standard Time` and `Eastern Standard Time
 * (Mexico)` have the identical `current_utc_offset` AND the identical
 * `is_currently_dst`, so a label built from those two facts cannot separate them at
 * all — which is exactly why `listTimeZones` returns the season-stable pair as
 * well, and why this reads that pair instead.
 *
 * Falls back to the live offset only when the pair is missing, so an older payload
 * degrades to a worse label rather than to no label.
 */
export function timeZoneLabel(zone) {
  const standard = zone?.standard_utc_offset;
  const daylight = zone?.daylight_utc_offset;
  if (!standard || !daylight) {
    return zone?.utc_offset ? `${zone.utc_offset}, ${zone.name}` : String(zone?.name ?? '');
  }
  const offsets = standard === daylight
    ? `${standard}, no daylight saving`
    : `${standard}, ${daylight} in summer`;
  return `${offsets}, ${zone.name}`;
}

/**
 * What a save is about to do, in the terms that matter.
 *
 * Two transitions, and both mean roughly the opposite of what the gesture looks
 * like. `acknowledgeField` is sent verbatim as a body key — the server refuses the
 * same two transitions with 422 unless it is present, so a rename on either side
 * turns the confirmation into an error nobody can clear.
 *
 * @returns {{kind: 'first-schedule'|'cleared'|'changed'|'none',
 *            hoursBefore: number, hoursAfter: number,
 *            needsConfirmation: boolean, acknowledgeField: string|null,
 *            message: string|null}}
 */
export function scheduleEffect(beforeRows, afterRows) {
  const hoursBefore = weeklyHours(beforeRows);
  const hoursAfter = weeklyHours(afterRows);
  const had = (beforeRows ?? []).length > 0;
  const has = (afterRows ?? []).length > 0;

  // GIVING AN AGENT THEIR FIRST WEEK NEEDS NO CONFIRMATION ANY MORE, and that is a
  // consequence of the routing rule changing rather than a relaxation of care.
  //
  // It used to be the dangerous direction: no schedule meant on shift all 168 hours, so
  // the first row anyone saved silently took away 160 of them. Now no schedule means on
  // shift never, so this save takes an agent who could receive nothing and makes them
  // available for `hoursAfter` — strictly an expansion, and the exact action every
  // queue needs performed for round-robin to work at all. Putting a dialog in front of
  // it would be friction on the thing we want done.
  if (!had && has) {
    return {
      kind: 'first-schedule', hoursBefore, hoursAfter,
      needsConfirmation: false, acknowledgeField: null, message: null,
    };
  }

  // CLEARING IS NOW THE DANGEROUS DIRECTION, and it is dangerous in the opposite way to
  // before. The old message said clearing does NOT take an agent off the rota; under
  // the current rule that is precisely what it does.
  if (had && !has) {
    return {
      kind: 'cleared',
      hoursBefore,
      hoursAfter,
      needsConfirmation: true,
      acknowledgeField: 'acknowledge_no_schedule',
      message: 'Clearing a schedule TAKES THIS AGENT OUT OF ROTATION. An agent with no '
        + `schedule is never on shift, so they go from ${formatHours(hoursBefore)} a week `
        + 'to receiving no tickets at all. If you meant to change their hours, edit the '
        + 'week instead of emptying it.',
    };
  }

  if (!had && !has) {
    return {
      kind: 'none', hoursBefore, hoursAfter,
      needsConfirmation: false, acknowledgeField: null, message: null,
    };
  }

  return {
    kind: 'changed', hoursBefore, hoursAfter,
    needsConfirmation: false, acknowledgeField: null, message: null,
  };
}
